/**
 * Снимки моделей для карточек каталога: очередь, готовые картинки и кэш в браузере.
 *
 * Быстрый путь — готовая картинка рядом с моделью: `sofa.glb` → `sofa.webp` в той же папке
 * (`staticThumbUrl`). Каталог грузит только её, ~10 КБ, а не саму модель. Картинки снимает
 * та же студия: в dev-режиме `window.catalogThumbs.requestAll()` ставит в очередь все модели
 * каталога, готовые снимки лежат в Cache API — их сохраняют файлами рядом с моделями.
 * Нет картинки (модель добавили, а снимок не сделали) — снимаем на лету, как ниже.
 *
 * Снимок делает невидимая сцена `ModelThumbStudio` — по одной модели за раз, той же
 * подгонкой и светом, что окно предпросмотра. Готовый снимок кладётся в Cache API: при
 * следующем открытии каталога картинка берётся оттуда, модель не грузится. Сменился вид
 * снимков — поднимаем `THUMB_VERSION`, старый кэш больше не читается.
 * Без Cache API (страница не по https) снимки живут до перезагрузки.
 */
import { create } from "zustand";
import { CATALOG, CATALOG_KEYS, type View3D } from "@/shared/catalog";
import { isDev } from "@/shared/config";

const THUMB_VERSION = 2;
const CACHE_NAME = `catalog-thumbs-v${THUMB_VERSION}`;

/** Ключ снимка: файл модели или параметры модели кодом — одинаковые модели снимаются один раз. */
export const thumbKey = (view3d: View3D): string => {
	switch (view3d.type) {
		case "model":
			return `model:${view3d.url}:${view3d.turn ?? 0}`;
		case "component":
			return `component:${view3d.id}:${view3d.width}x${view3d.height}x${view3d.depth}:${view3d.color}`;
		default:
			return `${view3d.type}:${view3d.color}`;
	}
};

export interface ThumbJob {
	key: string;
	view3d: View3D;
}

interface ThumbsState {
	/** Готовые снимки: url картинки; null — модель не снялась (покажем символ) */
	urls: Record<string, string | null>;
	/** Что ещё снять — по порядку */
	queue: ThumbJob[];
	/** Ключи, которые сейчас ищем в кэше */
	looking: Record<string, true>;
}

export const useThumbsStore = create<ThumbsState>(() => ({ urls: {}, queue: [], looking: {} }));

const cacheRequest = (key: string) =>
	new Request(`${location.origin}/__catalog-thumbs/${encodeURIComponent(key)}`);

const openCache = async (): Promise<Cache | null> => {
	try {
		return typeof caches === "undefined" ? null : await caches.open(CACHE_NAME);
	} catch {
		return null;
	}
};

/** Снимок нужен карточке: из кэша, а нет там — в очередь на съёмку. */
export const requestThumb = async (view3d: View3D) => {
	const key = thumbKey(view3d);
	const state = useThumbsStore.getState();
	if (key in state.urls || state.looking[key] || state.queue.some((job) => job.key === key)) return;
	useThumbsStore.setState({ looking: { ...state.looking, [key]: true } });
	const cache = await openCache();
	const hit = cache ? await cache.match(cacheRequest(key)).catch(() => undefined) : undefined;
	const blob = hit ? await hit.blob().catch(() => null) : null;
	useThumbsStore.setState((s) => {
		const looking = { ...s.looking };
		delete looking[key];
		return blob
			? { looking, urls: { ...s.urls, [key]: URL.createObjectURL(blob) } }
			: { looking, queue: [...s.queue, { key, view3d }] };
	});
};

/** Студия сняла модель (или не смогла — `blob` null): картинку — карточкам и в кэш. */
export const finishThumb = async (key: string, blob: Blob | null) => {
	useThumbsStore.setState((s) => ({
		urls: { ...s.urls, [key]: blob ? URL.createObjectURL(blob) : null },
		queue: s.queue.filter((job) => job.key !== key),
	}));
	if (!blob) return;
	const cache = await openCache();
	await cache
		?.put(cacheRequest(key), new Response(blob, { headers: { "Content-Type": blob.type } }))
		.catch(() => undefined);
};

/** Готовая картинка модели-файла рядом с ней; у модели кодом и примитивов — нет. */
export const staticThumbUrl = (view3d: View3D | undefined): string | null =>
	view3d?.type === "model" ? view3d.url.replace(/\.glb$/i, ".webp") : null;

/** Все модели-файлы каталога, по одной на файл — для съёмки картинок разом. */
const catalogModels = (): View3D[] => {
	const byUrl = new Map<string, View3D>();
	for (const key of CATALOG_KEYS) {
		for (const item of CATALOG[key].items) {
			const view3d = "view3d" in item ? (item.view3d as View3D | undefined) : undefined;
			if (view3d?.type === "model" && !byUrl.has(view3d.url)) byUrl.set(view3d.url, view3d);
		}
	}
	return [...byUrl.values()];
};

// В dev-режиме из консоли (или скриптом): снять картинки всех моделей и забрать их из кэша
if (isDev && typeof window !== "undefined") {
	(window as unknown as Record<string, unknown>).catalogThumbs = {
		requestAll: () => {
			const models = catalogModels();
			for (const view3d of models) void requestThumb(view3d);
			return models.length;
		},
		pending: () => useThumbsStore.getState().queue.length,
		cacheName: CACHE_NAME,
	};
}
